import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Clock, AlertTriangle, Info, X, ChevronRight, Circle } from "lucide-react";
import { FiSearch, FiX, FiInbox, FiMail } from 'react-icons/fi';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { IoCheckmarkDone } from "react-icons/io5";
import Layout from "@/components/Layout";
import { motion } from "framer-motion";

const notificationTypes = {
    success: {
        icon: Check,
        label: "Success",
        className: "text-green-500 bg-green-500/10",
    },
    warning: {
        icon: AlertTriangle,
        label: "Warning", 
        className: "text-yellow-500 bg-yellow-500/10",
    },
    error: {
        icon: AlertTriangle,
        label: "Error",
        className: "text-red-500 bg-red-500/10",
    },
    info: {
        icon: Info,
        label: "Info",
        className: "text-blue-500 bg-blue-500/10",
    },
};

const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const NotificationRow = ({ notification, onToggleRead }) => {
    const navigate = useNavigate();
    const { axiosInstance, setUnreadCount } = useAuth();
    const typeConfig = notificationTypes[notification.type] || notificationTypes.info;
    const Icon = typeConfig.icon;

    const handleToggleRead = async (e) => {
        e.stopPropagation();
        
        const newReadState = !notification.read;
        
        try {
            // Optimistic update
            setUnreadCount(prev => newReadState ? Math.max(0, prev - 1) : prev + 1);
            onToggleRead(notification._id, newReadState);
            
            await axiosInstance.post(`/notifications/${notification._id}/toggle-read`);
        } catch (error) {
            // Revert on error
            setUnreadCount(prev => newReadState ? prev + 1 : Math.max(0, prev - 1));
            onToggleRead(notification._id, !newReadState);
            console.error('Error toggling notification read status:', error);
        }
    };

    const handleGoToTask = (e) => {
        e.stopPropagation();
        if (notification.metadata?.taskKey) {
            navigate(`/${notification.organizationId.slug}/plan/${notification.metadata.taskKey}`);
        } else if (notification.metadata?.monitorId) {
            navigate(`/monitors/${notification.metadata.monitorId}`);
        }
    };

    const handleClick = async () => {
        // Mark as read on click (only if unread)
        if (!notification.read) {
            try {
                setUnreadCount(prev => Math.max(0, prev - 1));
                onToggleRead(notification._id, true);
                await axiosInstance.post(`/notifications/${notification._id}/mark-read`);
            } catch (error) {
                setUnreadCount(prev => prev + 1);
                onToggleRead(notification._id, false);
                console.error('Error marking notification as read:', error);
            }
        }

        // Navigate if there's a task or monitor
        if (notification.metadata?.taskKey) {
            navigate(`/${notification.organizationId.slug}/plan/${notification.metadata.taskKey}`);
        } else if (notification.metadata?.monitorId) {
            navigate(`/monitors/${notification.metadata.monitorId}`);
        }
    };

    return (
        <div
            onClick={handleClick}
            className={cn(
                "group flex items-center gap-3 px-3 py-2.5 border-b border-border/40 transition-colors cursor-pointer",
                "hover:bg-muted/40",
                !notification.read && "bg-primary/[0.03]"
            )}
        >
            {/* Icon with unread indicator */}
            <div className="relative shrink-0">
                <div className={cn("rounded-md p-1.5", typeConfig.className)}>
                    <Icon className="size-3.5" />
                </div>
                {!notification.read && (
                    <div className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-primary ring-2 ring-background" />
                )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <p className={cn(
                    "text-sm truncate",
                    !notification.read ? "font-medium" : "text-foreground"
                )}>
                    {notification.title}
                </p>
                <p className="text-[13px] text-muted-foreground truncate mt-0.5">
                    {notification.message}
                </p>
                <div className="flex items-center gap-1.5 mt-1 text-[11px] text-muted-foreground/70">
                    <span className="tabular-nums">{formatRelativeTime(notification.timestamp)}</span>
                    {notification.organizationId?.name && (
                        <>
                            <span className="text-muted-foreground/40">•</span>
                            <span>{notification.organizationId.name}</span>
                        </>
                    )}
                    {notification.metadata?.taskKey && (
                        <>
                            <span className="text-muted-foreground/40">•</span>
                            <span className="text-primary/80 font-medium">{notification.metadata.taskKey}</span>
                        </>
                    )}
                </div>
            </div>

            {/* Actions */}
            <TooltipProvider delayDuration={200}>
                <div className="flex items-center gap-1 shrink-0">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={handleToggleRead}
                                className={cn(
                                    "p-1.5 rounded-md border transition-all",
                                    "opacity-0 group-hover:opacity-100",
                                    notification.read 
                                        ? "border-transparent hover:border-border hover:bg-muted text-muted-foreground hover:text-foreground"
                                        : "border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/30 opacity-100"
                                )}
                            >
                                {notification.read ? <Circle className="size-3.5" /> : <Check className="size-3.5" />}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="text-xs">
                            {notification.read ? "Mark as unread" : "Mark as read"}
                        </TooltipContent>
                    </Tooltip>
                    {(notification.metadata?.taskKey || notification.metadata?.monitorId) && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={handleGoToTask}
                                    className="p-1.5 rounded-md border border-transparent hover:border-border hover:bg-muted text-muted-foreground/50 hover:text-foreground transition-all"
                                >
                                    <ChevronRight className="size-3.5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="text-xs">
                                {notification.metadata?.taskKey ? 'Go to task' : 'Go to monitor'}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </TooltipProvider>
        </div>
    );
};

const NotificationsPage = () => {
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [selectedType, setSelectedType] = useState(null); // null = all types
    const { axiosInstance, notifications, setNotifications, fetchUnreadCount } = useAuth();

    useEffect(() => {
        fetchNotifications();
        fetchUnreadCount();
    }, []);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/notifications');
            setNotifications(response.data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAllAsRead = async () => {
        try {
            await axiosInstance.post('/notifications/mark-all-read');
            setNotifications(prev =>
                prev.map(notif => ({ ...notif, read: true }))
            );
            fetchUnreadCount();
        } catch (error) {
            console.error('Error marking notifications as read:', error);
        }
    };

    const handleNotificationToggle = (notificationId, newReadState) => {
        setNotifications(prev =>
            prev.map(notif =>
                notif._id === notificationId
                    ? { ...notif, read: newReadState }
                    : notif
            )
        );
    };

    // Filtered notifications
    const filteredNotifications = useMemo(() => {
        return notifications.filter(notif => {
            // Search filter
            if (searchTerm) {
                const query = searchTerm.toLowerCase();
                const matchesTitle = notif.title?.toLowerCase().includes(query);
                const matchesMessage = notif.message?.toLowerCase().includes(query);
                const matchesOrg = notif.organizationId?.name?.toLowerCase().includes(query);
                const matchesTaskKey = notif.metadata?.taskKey?.toLowerCase().includes(query);
                if (!matchesTitle && !matchesMessage && !matchesOrg && !matchesTaskKey) {
                    return false;
                }
            }

            // Unread filter
            if (unreadOnly && notif.read) return false;

            // Type filter
            if (selectedType && notif.type !== selectedType) return false;

            return true;
        });
    }, [notifications, searchTerm, unreadOnly, selectedType]);

    const unreadCount = notifications.filter(n => !n.read).length;
    
    const clearAllFilters = () => {
        setSearchTerm('');
        setUnreadOnly(false);
        setSelectedType(null);
    };

    // Get active filter details for badges
    const getActiveFilterDetails = () => {
        const details = [];
        
        if (unreadOnly) {
            details.push({ 
                id: 'unread', 
                label: 'Unread only',
                type: 'Status',
                onRemove: () => setUnreadOnly(false) 
            });
        }
        
        if (selectedType) {
            details.push({ 
                id: 'type', 
                label: notificationTypes[selectedType]?.label || selectedType,
                type: 'Type',
                onRemove: () => setSelectedType(null) 
            });
        }
        
        return details;
    };

    const activeFilters = getActiveFilterDetails();

    const fadeInVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1 }
    };

    return (
        <Layout>
            <motion.div
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.5 }}
                className="space-y-4 mb-8"
            >
                {/* Header Section */}
                <div className="space-y-4">
                    {/* Top Row - Title and Actions */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-semibold">Notifications</h1>
                            <p className="text-sm text-muted-foreground mt-1">
                                {notifications.length > 0 ? (
                                    <>
                                        {filteredNotifications.length.toLocaleString()} {filteredNotifications.length === 1 ? 'notification' : 'notifications'}
                                        {unreadCount > 0 && (
                                            <span className="ml-1">
                                                • {unreadCount} unread
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    'No notifications'
                                )}
                            </p>
                        </div>
                        
                        {/* Mark All Read */}
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={markAllAsRead}
                            disabled={unreadCount === 0}
                        >
                            <IoCheckmarkDone className="size-4 mr-1.5" />
                            Mark all read
                        </Button>
                    </div>

                    {/* Filter Bar */}
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Search Input */}
                        <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 pointer-events-none" />
                            <Input
                                placeholder="Search notifications..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-8 h-9 w-full sm:w-64 text-sm"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded hover:bg-muted/50"
                                >
                                    <FiX className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>

                        {/* Unread Only Toggle */}
                        <button 
                            onClick={() => setUnreadOnly(!unreadOnly)}
                            className={cn(
                                "flex items-center gap-1.5 px-3 h-9 rounded-md border transition-all duration-200 whitespace-nowrap text-sm font-medium",
                                unreadOnly 
                                    ? "border-primary/50 bg-primary/10 text-primary shadow-sm"
                                    : "border-muted-foreground/20 hover:bg-muted/50 hover:border-muted-foreground/30 bg-background"
                            )}
                        >
                            <FiMail className="h-4 w-4 flex-shrink-0" />
                            <span className="text-xs">Unread</span>
                            {unreadCount > 0 && (
                                <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 flex items-center justify-center p-0 px-1.5 text-xs">
                                    {unreadCount}
                                </Badge>
                            )}
                        </button>

                        {/* Type Filters */}
                        {Object.entries(notificationTypes).map(([type, config]) => (
                            <button
                                key={type}
                                onClick={() => setSelectedType(selectedType === type ? null : type)}
                                className={cn(
                                    "flex items-center gap-1.5 px-3 h-9 rounded-md border transition-all duration-200 whitespace-nowrap text-sm font-medium",
                                    selectedType === type
                                        ? cn(
                                            type === 'success' && "border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950/50 dark:text-green-300 shadow-sm",
                                            type === 'warning' && "border-yellow-300 bg-yellow-50 text-yellow-700 dark:border-yellow-700 dark:bg-yellow-950/50 dark:text-yellow-300 shadow-sm",
                                            type === 'error' && "border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/50 dark:text-red-300 shadow-sm",
                                            type === 'info' && "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/50 dark:text-blue-300 shadow-sm"
                                        )
                                        : "border-muted-foreground/20 hover:bg-muted/50 hover:border-muted-foreground/30 bg-background"
                                )}
                            >
                                <config.icon className="h-4 w-4 flex-shrink-0" />
                                <span className="text-xs">{config.label}</span>
                            </button>
                        ))}

                    </div>

                    {/* Active Filter Chips - Always Visible */}
                    <div className="h-12 flex flex-wrap items-center gap-2 py-2 px-3 bg-muted/20 border border-border/50 rounded-lg overflow-hidden">
                        {activeFilters.length > 0 ? (
                            <>
                                {activeFilters.map(filter => (
                                    <div
                                        key={filter.id}
                                        className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-muted/50 border border-muted-foreground/10 text-foreground transition-all hover:bg-muted"
                                    >
                                        {filter.type && (
                                            <span className="text-[10px] font-medium text-muted-foreground uppercase">
                                                {filter.type}
                                            </span>
                                        )}
                                        <span className="text-xs">{filter.label}</span>
                                        <button
                                            onClick={filter.onRemove}
                                            className="p-0.5 rounded hover:bg-background transition-colors text-muted-foreground hover:text-foreground"
                                            aria-label={`Remove ${filter.label} filter`}
                                        >
                                            <FiX className="h-2.5 w-2.5" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={clearAllFilters}
                                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    Clear all
                                </button>
                            </>
                        ) : (
                            <span className="text-xs text-muted-foreground">
                                No filters applied
                            </span>
                        )}
                    </div>
                </div>

                {/* Notifications List */}
                <div className="border rounded-lg overflow-hidden bg-card">
                    {loading ? (
                        <div className="divide-y divide-border/40">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="flex items-center gap-3 px-3 py-4">
                                    <Skeleton className="size-8 rounded-md shrink-0" />
                                    <div className="flex-1 space-y-2.5">
                                        <Skeleton className="h-4 w-3/4" />
                                        <Skeleton className="h-3.5 w-full" />
                                        <Skeleton className="h-3 w-2/5" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : filteredNotifications.length > 0 ? (
                        filteredNotifications.map((notification) => (
                            <NotificationRow
                                key={notification._id}
                                notification={notification}
                                onToggleRead={handleNotificationToggle}
                            />
                        ))
                    ) : notifications.length > 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <FiSearch className="size-10 text-muted-foreground/50 mb-4" />
                            <h3 className="text-base font-medium mb-1">No matching notifications</h3>
                            <p className="text-sm text-muted-foreground max-w-sm mb-4">
                                Try adjusting your search or filters
                            </p>
                            <Button variant="outline" size="sm" onClick={clearAllFilters}>
                                Clear all filters
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <FiInbox className="size-10 text-muted-foreground/50 mb-4" />
                            <h3 className="text-base font-medium mb-1">No notifications yet</h3>
                            <p className="text-sm text-muted-foreground max-w-sm">
                                When you receive notifications, they'll appear here.
                            </p>
                        </div>
                    )}
                </div>
            </motion.div>
        </Layout>
    );
};

export default NotificationsPage;
